"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  TextArea,
  CheckboxRow,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";
import type { PermitConfig, PermitItem } from "@/lib/permits/types";

/* Config-driven permit-to-work engine.

   Everything specific to a permit type lives in a PermitConfig object
   (lib/permits/*.ts). This component renders the permit header, validity
   window, the config sections (as checklists / inputs), the authorisation
   block and the close-out section, and produces a printable permit.

   IMPORTANT: this tool only *drafts* a permit form. It never issues a permit —
   only the named issuing authority on site can authorise work to start. */

/** Auto permit number: PREFIX-YYYYMMDD-HHMM, generated once per visit. */
function makePermitNumber(prefix: string): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${prefix}-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

function todayISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Clamp an end time to at most maxHours after the start time (same date). */
function clampEnd(start: string, end: string, maxHours: number): string {
  if (!start || !end) return end;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const startMin = sh * 60 + sm;
  let endMin = eh * 60 + em;
  if (endMin <= startMin) endMin += 24 * 60; // crosses midnight
  const maxMin = startMin + maxHours * 60;
  if (endMin > maxMin) {
    const clamped = maxMin % (24 * 60);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${p(Math.floor(clamped / 60))}:${p(clamped % 60)}`;
  }
  return end;
}

function defaultEnd(start: string, hours: number): string {
  const [sh, sm] = start.split(":").map(Number);
  const total = (sh * 60 + sm + hours * 60) % (24 * 60);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(Math.floor(total / 60))}:${p(total % 60)}`;
}

/** A small signature/date line for the printed permit. */
function SignLine({ name, role }: { name: string; role: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-end gap-4">
      <div>
        <div className="mt-5 border-b border-dashed border-ink/50" />
        <div className="mt-1 text-[0.72rem] uppercase tracking-wide text-muted">
          {name} {role && <span className="normal-case">— {role}</span>}
        </div>
      </div>
      <div>
        <div className="mt-5 w-28 border-b border-dashed border-ink/50" />
        <div className="mt-1 text-[0.72rem] uppercase tracking-wide text-muted">Date / time</div>
      </div>
    </div>
  );
}

export function PermitForm({ config }: { config: PermitConfig }) {
  const [permitNo] = useState(() => makePermitNumber(config.permitNumberPrefix));
  const tracked = useRef(false);

  // Permit header
  const [site, setSite] = useState("");
  const [location, setLocation] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [company, setCompany] = useState("");

  // Validity window
  const [date, setDate] = useState(todayISO());
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState(() => defaultEnd("08:00", config.validity.defaultHours));

  // Authorisation
  const [issuerName, setIssuerName] = useState("");
  const [issuerRole, setIssuerRole] = useState("");
  const [acceptorName, setAcceptorName] = useState("");

  // Section item state (checks + text/textarea), keyed by item id.
  const [values, setValues] = useState<Record<string, string | boolean>>({});

  const allItems: PermitItem[] = useMemo(
    () => config.sections.flatMap((s) => s.items),
    [config]
  );
  const criticalChecks = useMemo(
    () => allItems.filter((i) => i.type === "check" && i.critical),
    [allItems]
  );

  const outstanding = criticalChecks.filter((i) => values[i.id] !== true).length;
  const ready = criticalChecks.length > 0 && outstanding === 0;

  // Everything a permit must carry before it leaves the site office — header
  // fields, the validity window, every critical check and the named people.
  // Drives the completion panel and the print guard.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Site", done: site.trim().length > 0 },
    { label: "Exact location of work", done: location.trim().length > 0 },
    { label: "Issued to (person doing the work)", done: issuedTo.trim().length > 0 },
    { label: "Validity window (date, start and end time)", done: Boolean(date && startTime && endTime) },
    ...criticalChecks.map((i) => ({
      label: `Critical check: ${i.label}`,
      done: values[i.id] === true,
    })),
    { label: "Issuing authority (name)", done: issuerName.trim().length > 0 },
    { label: "Permit accepted by (name)", done: acceptorName.trim().length > 0 },
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  const setVal = (id: string, v: string | boolean) =>
    setValues((prev) => ({ ...prev, [id]: v }));

  const onEndBlur = () =>
    setEndTime((e) => clampEnd(startTime, e, config.validity.maxHours));

  // Fire the funnel event once when the user has engaged with the permit.
  useEffect(() => {
    if (!tracked.current && (site || location || criticalChecks.some((i) => values[i.id] === true))) {
      tracked.current = true;
      trackToolResult(config.slug, {});
    }
  }, [site, location, values, criticalChecks, config.slug]);

  const dateLabel = date
    ? new Date(date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* ── Form (screen only) ───────────────────────────────────────────── */}
      <div className="print-hide space-y-5">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">{config.permitTitle} — details</h2>
          <p className="mt-1 text-sm text-muted">
            Fill in the permit, then print it for wet-ink signature. This tool drafts the form — only the
            named issuing authority on site can issue the permit and authorise work to start.
          </p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Permit number">
              <TextInput value={permitNo} readOnly aria-label="Permit number" />
            </Field>
            <Field label="Site">
              <RememberedTextInput store="sites" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Riverside Phase 2" />
            </Field>
            <Field label="Exact location of work" className="sm:col-span-2">
              <TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Flat roof, Block C, bay 3" />
            </Field>
            <Field label="Issued to (person)">
              <RememberedTextInput store="people" value={issuedTo} onChange={(e) => setIssuedTo(e.target.value)} placeholder="Name of person doing the work" />
            </Field>
            <Field label="Company">
              <TextInput value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Their employer / contractor" />
            </Field>
          </div>
          <div className="mt-4">
            <RememberedNamesNote stores={["people", "sites"]} />
          </div>
        </div>

        {/* Validity window */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Validity window</h2>
          <p className="mt-1 text-sm text-muted">{config.validity.note}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Date">
              <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Start time">
              <TextInput type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </Field>
            <Field label="End time" hint={`Maximum ${config.validity.maxHours} hours.`}>
              <TextInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} onBlur={onEndBlur} />
            </Field>
          </div>
        </div>

        {/* Config sections */}
        {config.sections.map((section) => (
          <div key={section.heading} className="card shadow-soft p-6">
            <h2 className="text-xl">{section.heading}</h2>
            {section.intro && <p className="mt-1 text-sm text-muted">{section.intro}</p>}
            <div className="mt-4 space-y-3">
              {section.items.map((item) => {
                if (item.type === "check") {
                  return (
                    <div key={item.id}>
                      <CheckboxRow
                        checked={values[item.id] === true}
                        onChange={(v) => setVal(item.id, v)}
                      >
                        {item.critical && (
                          <span className="mr-1.5 rounded bg-risk-veryhigh/10 px-1.5 py-0.5 text-[0.68rem] font-bold uppercase tracking-wide text-risk-veryhigh">
                            Critical
                          </span>
                        )}
                        {item.label}
                        {item.hint && <span className="mt-0.5 block text-[0.78rem] text-muted">{item.hint}</span>}
                      </CheckboxRow>
                    </div>
                  );
                }
                return (
                  <Field key={item.id} label={item.label} hint={item.hint}>
                    {item.type === "textarea" ? (
                      <TextArea
                        value={(values[item.id] as string) ?? ""}
                        onChange={(e) => setVal(item.id, e.target.value)}
                      />
                    ) : (
                      <TextInput
                        value={(values[item.id] as string) ?? ""}
                        onChange={(e) => setVal(item.id, e.target.value)}
                      />
                    )}
                  </Field>
                );
              })}
            </div>
          </div>
        ))}

        {/* Authorisation */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Authorisation</h2>
          <p className="mt-1 text-sm text-muted">
            Signed on the printed permit. The issuing authority confirms the controls are in place; the
            person accepting the permit confirms they understand and will work to it.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Issuing authority (name)">
              <RememberedTextInput store="people" value={issuerName} onChange={(e) => setIssuerName(e.target.value)} placeholder="Who is issuing the permit" />
            </Field>
            <Field label="Issuing authority (role)">
              <TextInput value={issuerRole} onChange={(e) => setIssuerRole(e.target.value)} placeholder="e.g. Site Manager" />
            </Field>
            <Field label="Permit accepted by (name)" className="sm:col-span-2">
              <RememberedTextInput store="people" value={acceptorName} onChange={(e) => setAcceptorName(e.target.value)} placeholder="Person accepting the permit" />
            </Field>
          </div>
        </div>
      </div>

      {/* ── Status + printable permit ────────────────────────────────────── */}
      <div className="space-y-4">
        {/* Sticky on large screens so progress + next gap stay visible while
            scrolling the long form. */}
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Permit completion" />
        </div>
        <div className="print-hide">
          {ready ? (
            <ResultBanner level="low" title="Ready for issuing authority review">
              All critical checks are ticked. Print the permit for the issuing authority to review, sign and
              issue on site — this tool does not issue the permit.
            </ResultBanner>
          ) : (
            <ResultBanner
              level="veryhigh"
              title={`PERMIT NOT READY TO ISSUE — ${outstanding} critical check${outstanding === 1 ? "" : "s"} outstanding`}
            >
              Complete every critical control before this permit is put forward for issue.
            </ResultBanner>
          )}
        </div>

        {/* The printable permit. */}
        <div className="print-area card shadow-soft p-6" id="permit-document">
          {/* Bordered header with permit number + validity prominent */}
          <div className="rounded-xl border-2 border-ink/80 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[0.72rem] font-semibold uppercase tracking-wide text-emerald-deep">
                  Permit to Work
                </div>
                <h2 className="mt-0.5 font-display text-xl font-bold text-ink">{config.permitTitle}</h2>
              </div>
              <div className="text-right">
                <div className="text-[0.7rem] uppercase tracking-wide text-muted">Permit no.</div>
                <div className="font-display text-lg font-bold text-ink">{permitNo}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-line pt-3 text-[0.84rem]">
              <div><span className="text-muted">Site:</span> <span className="font-semibold text-ink">{site || "—"}</span></div>
              <div><span className="text-muted">Issued to:</span> <span className="font-semibold text-ink">{issuedTo || "—"}{company ? ` (${company})` : ""}</span></div>
              <div className="col-span-2"><span className="text-muted">Location of work:</span> <span className="font-semibold text-ink">{location || "—"}</span></div>
            </div>
            {/* Validity window — prominent */}
            <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-mist px-3 py-2 text-center text-[0.82rem]">
              <div>
                <div className="text-muted">Date</div>
                <div className="font-bold text-ink">{dateLabel || "—"}</div>
              </div>
              <div>
                <div className="text-muted">Valid from</div>
                <div className="font-bold text-ink">{startTime || "—"}</div>
              </div>
              <div>
                <div className="text-muted">Valid to</div>
                <div className="font-bold text-ink">{endTime || "—"}</div>
              </div>
            </div>
            <p className="mt-2 text-[0.74rem] leading-snug text-muted">{config.validity.note}</p>
            {endTime && (
              <p className="mt-2 rounded-md bg-ink px-3 py-1.5 text-center text-[0.78rem] font-bold uppercase tracking-wide text-white">
                This permit expires at {endTime} — work must stop
              </p>
            )}
          </div>

          {/* Standing warnings */}
          {config.warnings && config.warnings.length > 0 && (
            <div className="mt-4 rounded-lg border border-risk-veryhigh/40 bg-[#fdecec] px-4 py-3 text-[0.82rem] text-[#a31616]">
              <ul className="list-disc space-y-1 pl-4">
                {config.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Status strip (also printed) */}
          <div className="mt-4">
            <ResultBanner
              level={ready ? "low" : "veryhigh"}
              title={
                ready
                  ? "All critical checks complete — for issuing authority review"
                  : `NOT READY TO ISSUE — ${outstanding} critical check${outstanding === 1 ? "" : "s"} outstanding`
              }
            />
          </div>

          {/* Section checklists */}
          {config.sections.map((section) => (
            <div key={section.heading} className="mt-5 avoid-break">
              <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
                {section.heading}
              </h3>
              <ul className="mt-2 space-y-1.5 text-[0.86rem]">
                {section.items.map((item) => {
                  if (item.type === "check") {
                    const checked = values[item.id] === true;
                    return (
                      <li key={item.id} className="flex items-start gap-2">
                        <span
                          className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            checked ? "border-emerald bg-emerald text-white" : "border-ink/50"
                          }`}
                          aria-hidden
                        >
                          {checked ? "✓" : ""}
                        </span>
                        <span className="text-body">
                          {item.critical && <span className="font-bold text-risk-veryhigh">[Critical] </span>}
                          {item.label}
                        </span>
                      </li>
                    );
                  }
                  const text = (values[item.id] as string) || "";
                  return (
                    <li key={item.id} className="text-body">
                      <span className="text-muted">{item.label}:</span>{" "}
                      {text ? <span className="text-ink">{text}</span> : <span className="text-ink">__________________________</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Authorisation block — signature lines */}
          <div className="mt-6 avoid-break rounded-lg border border-line p-4">
            <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">Authorisation</h3>
            <p className="mt-1 text-[0.78rem] text-muted">
              By signing, the issuing authority confirms the controls above are in place. The permit holder
              confirms they understand the conditions and will work to them.
            </p>
            <SignLine name={issuerName || "Issuing authority"} role={issuerRole || "role"} />
            <SignLine name={acceptorName || "Permit accepted by"} role="permit holder" />
          </div>

          {/* Close-out / cancellation — empty to complete */}
          <div className="mt-4 avoid-break rounded-lg border border-line p-4">
            <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
              Close-out & cancellation
            </h3>
            <p className="mt-1 text-[0.78rem] text-muted">
              Completed on site when the work and any post-work watch are finished. The permit is only
              cancelled once every line is confirmed.
            </p>
            <ul className="mt-2 space-y-1.5 text-[0.86rem]">
              {config.closeOut.map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-ink/50" aria-hidden />
                  <span className="text-body">{c.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <SignLine name="Cancelled by" role="name / role" />
            </div>
          </div>

          <PrintFooter toolName={`${config.permitTitle.toLowerCase()} tool`} />
        </div>

        <div className="print-hide">
          <GuardedPrintButton
            tool={config.slug}
            label="Print / save permit as PDF"
            missing={missingRequired}
            warningTitle="This permit still has missing required fields or unchecked critical controls"
          />
        </div>
      </div>
    </div>
  );
}
